import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { AuthModal } from '../components/AuthModal'
import { Button } from '../components/ui/button'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import {
  ArrowRight,
  Wallet,
  Layers,
  ShieldCheck,
  Globe,
  BarChart3,
  Building2,
  Gem,
  Landmark,
  PieChart,
  FileCheck,
  Handshake,
  Lock,
  Scale,
  ChevronDown,
  Zap,
  TrendingUp,
  Users,
  DollarSign,
  CircleDot
} from 'lucide-react'

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }

export default function HowItWorksPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { tenant } = useTenant()
  const [authOpen, setAuthOpen] = useState(false)

  const steps = [
    {
      num: '01',
      icon: FileCheck,
      title: t('howItWorks.steps.structure.title'),
      description: t('howItWorks.steps.structure.description'),
    },
    {
      num: '02',
      icon: Layers,
      title: t('howItWorks.steps.tokenize.title'),
      description: t('howItWorks.steps.tokenize.description'),
    },
    {
      num: '03',
      icon: Wallet,
      title: t('howItWorks.steps.invest.title'),
      description: t('howItWorks.steps.invest.description'),
    },
  ]

  const benefits = [
    { icon: PieChart, title: t('howItWorks.benefits.fractional.title'), description: t('howItWorks.benefits.fractional.description') },
    { icon: Zap, title: t('howItWorks.benefits.liquidity.title'), description: t('howItWorks.benefits.liquidity.description') },
    { icon: Globe, title: t('howItWorks.benefits.access.title'), description: t('howItWorks.benefits.access.description') },
    { icon: DollarSign, title: t('howItWorks.benefits.cost.title'), description: t('howItWorks.benefits.cost.description') },
  ]

  const assetCategories = [
    { icon: Building2, label: t('howItWorks.assets.realEstate') },
    { icon: Gem, label: t('howItWorks.assets.commodities') },
    { icon: Landmark, label: t('howItWorks.assets.funds') },
    { icon: BarChart3, label: t('howItWorks.assets.equity') },
  ]

  const services = [
    { icon: Scale, title: t('howItWorks.services.broker.title'), description: t('howItWorks.services.broker.description') },
    { icon: ShieldCheck, title: t('howItWorks.services.custody.title'), description: t('howItWorks.services.custody.description') },
    { icon: Handshake, title: t('howItWorks.services.partners.title'), description: t('howItWorks.services.partners.description') },
    { icon: Lock, title: t('howItWorks.services.security.title'), description: t('howItWorks.services.security.description') },
  ]

  const marketStats = [
    { value: '$16T', label: t('howItWorks.market.projected') },
    { value: '24/7', label: t('howItWorks.market.trading') },
    { value: '100x', label: t('howItWorks.market.cheaper') },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center bg-brand-secondary overflow-hidden" data-testid="hiw-hero">
        <div className="absolute inset-0 hero-bg-kenburns">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80&auto=format"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-secondary/90 via-brand-secondary/80 to-brand-secondary/95" />
        <div className="absolute inset-0 hero-radial-glow" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-8 backdrop-blur-sm">
              {t('howItWorks.hero.badge')}
            </motion.span>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
              {t('howItWorks.hero.title1')}{' '}
              <span className="text-brand-primary hero-text-glow">{t('howItWorks.hero.title2')}</span>
            </motion.h1>

            <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ delay: 0.3 }}
              className="w-16 h-px bg-brand-primary mx-auto my-8 shadow-[0_0_12px_rgba(var(--brand-primary-rgb)/0.6)]" />

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className="text-lg text-white/70 max-w-2xl mx-auto font-body drop-shadow-md">
              {t('howItWorks.hero.subtitle')}
            </motion.p>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <ChevronDown className="w-6 h-6 text-brand-primary animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* What is an RWA Token */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="hiw-rwa-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.span variants={fadeUp}
                className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-6">
                {t('howItWorks.rwa.badge')}
              </motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-heading text-brand-secondary mb-6">
                {t('howItWorks.rwa.title')}
              </motion.h2>
              <motion.div variants={fadeUp} className="w-16 h-px bg-brand-primary mb-6" />
              <motion.p variants={fadeUp} className="text-slate-600 leading-relaxed mb-4">
                {t('howItWorks.rwa.p1')}
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-600 leading-relaxed">
                {t('howItWorks.rwa.p2')}
              </motion.p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="relative">
              <div className="bg-brand-secondary p-8 lg:p-10">
                <div className="space-y-6">
                  {[
                    { icon: ShieldCheck, text: t('howItWorks.rwa.point1') },
                    { icon: Layers, text: t('howItWorks.rwa.point2') },
                    { icon: Globe, text: t('howItWorks.rwa.point3') },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="w-10 h-10 bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-brand-primary" />
                      </div>
                      <p className="text-white/80 text-sm leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-brand-primary/20 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Process - 3 Steps */}
      <section className="py-24 bg-white" data-testid="hiw-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('howItWorks.process.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('howItWorks.process.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('howItWorks.process.subtitle')}</p>
          </div>

          <div className="relative">
            {/* Connection line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary/30 to-transparent -translate-y-1/2" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }} viewport={{ once: true }}
                  className="relative bg-white border border-slate-100 p-8 hover:border-brand-primary/30 transition-all duration-300 group"
                  data-testid={`hiw-step-${index + 1}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-5xl font-heading text-brand-primary/20 group-hover:text-brand-primary/40 transition-colors">{step.num}</span>
                    <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
                      <step.icon className="w-7 h-7 text-brand-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-heading text-brand-secondary mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-24 bg-brand-secondary" data-testid="hiw-market-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('howItWorks.market.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-white">{t('howItWorks.market.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-white/60 mt-4 max-w-xl mx-auto">{t('howItWorks.market.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {marketStats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                className="text-center p-8 border border-white/10 hover:border-brand-primary/30 transition-colors">
                <p className="text-4xl lg:text-5xl font-heading text-brand-primary mb-2">{stat.value}</p>
                <p className="text-sm text-white/60 uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-center text-white/50 max-w-2xl mx-auto text-sm leading-relaxed">
            {t('howItWorks.market.disclaimer')}
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="hiw-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('howItWorks.benefits.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('howItWorks.benefits.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }} viewport={{ once: true }}
                className="p-6 bg-white border border-slate-100 hover:border-brand-primary/30 transition-all duration-300 group"
                data-testid={`hiw-benefit-${index}`}>
                <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center mb-4 group-hover:bg-brand-primary/20 transition-colors">
                  <benefit.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="text-lg font-heading text-brand-secondary mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Asset Categories */}
      <section className="py-24 bg-white" data-testid="hiw-assets-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-6">
                {t('howItWorks.assets.badge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary mb-6">{t('howItWorks.assets.title')}</h2>
              <div className="w-16 h-px bg-brand-primary mb-6" />
              <p className="text-slate-600 leading-relaxed mb-8">{t('howItWorks.assets.description')}</p>
              <Button onClick={() => navigate('/marketplace')}
                className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none"
                data-testid="hiw-explore-assets-btn">
                {t('howItWorks.assets.cta')}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {assetCategories.map((cat, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                  className="p-6 bg-brand-secondary flex flex-col items-center justify-center gap-3 aspect-square hover:bg-brand-secondary/90 transition-colors group"
                  data-testid={`hiw-asset-${i}`}>
                  <cat.icon className="w-10 h-10 text-brand-primary group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-semibold text-white/80 tracking-wide uppercase">{cat.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="hiw-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('howItWorks.services.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('howItWorks.services.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('howItWorks.services.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }} viewport={{ once: true }}
                className="flex gap-5 p-6 bg-white border border-slate-100 hover:border-brand-primary/30 transition-all duration-300 group"
                data-testid={`hiw-service-${index}`}>
                <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-primary/20 transition-colors">
                  <service.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-heading text-brand-secondary mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-secondary" data-testid="hiw-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
            {t('howItWorks.cta.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-white mb-4">{t('howItWorks.cta.title')}</h2>
          <div className="w-16 h-px bg-brand-primary mx-auto my-6" />
          <p className="text-white/60 max-w-xl mx-auto mb-10">{t('howItWorks.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => isAuthenticated ? navigate('/marketplace') : setAuthOpen(true)}
              className="h-16 px-10 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none shadow-[0_0_30px_rgba(var(--brand-primary-rgb)/0.3)]"
              data-testid="hiw-cta-invest-btn">
              <Wallet className="w-5 h-5 mr-2" />{t('howItWorks.cta.investBtn')}<ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/marketplace')} variant="outline"
              className="h-16 px-10 bg-transparent border-white/20 text-white hover:bg-white/10 hover:border-white/30 text-base rounded-none backdrop-blur-sm"
              data-testid="hiw-cta-explore-btn">
              {t('howItWorks.cta.exploreBtn')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-brand-secondary border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={tenant?.branding?.logo_url || "https://uni2ecosystem.com/images/logos/tokenizelogo.png"} alt={tenant?.name || "2kenize"} className="h-8 w-auto" />
            <p className="text-sm text-white/50">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  )
}
